import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext.js';
import { useNavigate } from 'react-router-dom';
import api from '../services/api.js';
import RecordDetails from '../components/RecordDetails.jsx';
import UploadForm from '../components/UploadForm.jsx';
import RecordsTable from '../components/RecordsTable.jsx';
import DuplicateRecordsTable from '../components/DuplicateRecordsTable.jsx'; // IMPORT ADDED
import ManualRecordForm from '../components/ManualRecordForm.jsx';
import ScheduleCalendar from '../components/ScheduleCalendar.jsx';
import Header from '../components/Header.jsx';
import SummaryTab from '../components/SummaryTab.jsx';
import RegisterUserForm from '../components/RegisterUserForm.jsx';
import ManageUsers from '../components/ManageUsers.jsx';
import OverdueTasksTab from '../components/OverdueTasksTab.jsx';
import ConfirmationModal from '../components/ConfirmationModal.jsx';
import { Bell, Upload, Edit3, PlusCircle, Calendar, Users, UserPlus, FileText, BarChart, AlertTriangle } from 'lucide-react';
import Pagination from '../components/Pagination.jsx';

const AdminPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [records, setRecords] = useState([]);
  const [duplicateRecords, setDuplicateRecords] = useState([]); // NEW STATE
  const [collectors, setCollectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [duplicatesLoading, setDuplicatesLoading] = useState(false); // NEW STATE
  const [isUploading, setIsUploading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [uploadCollector, setUploadCollector] = useState('');
  const [filterCollector, setFilterCollector] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRecords, setSelectedRecords] = useState([]);
  // --- NEW STATE FOR ERROR POPUP ---
  const [failedRecords, setFailedRecords] = useState([]);
  // Modal State
  const [modalConfig, setModalConfig] = useState({isOpen: false,title: '',message: '',onConfirm: () => {},isDanger: false,confirmText: 'Confirm'
  });
  // --- Merge Selected Duplicates Modal State ---
  const [isMergeModalOpen, setIsMergeModalOpen] = useState(false);
  const [mergePrimaryId, setMergePrimaryId] = useState('');
  const [mergeSubmitting, setMergeSubmitting] = useState(false);

  // Pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [totalRecords, setTotalRecords] = useState(0);

  const [creationMode, setCreationMode] = useState('upload');
  const [manualAssignCollector, setManualAssignCollector] = useState('');

  const [viewMode, setViewMode] = useState('create');

  // Overdue tasks
  const [overdueTasks, setOverdueTasks] = useState([]);
  const [seenOverdueCount, setSeenOverdueCount] = useState(() => {
    return parseInt(localStorage.getItem('adminSeenOverdueCount') || '0', 10);
  });
  const [overdueLoading, setOverdueLoading] = useState(true);
  const [overdueError, setOverdueError] = useState('');

  // Persist seen count
  useEffect(() => {
    localStorage.setItem('adminSeenOverdueCount', seenOverdueCount.toString());
  }, [seenOverdueCount]);

  const showMessage = (text, type = 'error', duration = 5000) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), duration);
  };

  // Helpers
  const computeIsPastDue = useCallback((event) => {
    if (!event || !event.scheduledDate) return false;
    if (event.isCompleted) return false;

    const now = new Date();
    const d = new Date(event.scheduledDate);
    const base = new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 0, 0, 0, 0);

    if (event.scheduledTime) {
      const [hh, mm] = event.scheduledTime.split(':').map(Number);
      const end = new Date(base.getFullYear(), base.getMonth(), base.getDate(), hh || 0, (mm || 0) + 15, 0, 0);
      return now > end;
    }
    const eod = new Date(base.getFullYear(), base.getMonth(), base.getDate(), 23, 59, 59, 999);
    return now > eod;
  }, []);

  const fetchOverdueTasks = useCallback(async () => {
    setOverdueLoading(true);
    setOverdueError('');
    try {
      const response = await api.get('/records/overdue-events');

      if (Array.isArray(response.data) && response.data.length > 0) {
        setOverdueTasks(response.data);
      } else {
        const now = new Date();
        const start = new Date(now.getFullYear(), now.getMonth() - 2, 1);
        const alt = await api.get('/records/scheduled-events', {
          params: { startDate: start.toISOString(), endDate: now.toISOString() }
        });
        const events = Array.isArray(alt.data) ? alt.data : [];
        const overdue = events.filter(e => computeIsPastDue(e));
        setOverdueTasks(overdue);
      }
    } catch (err) {
      console.error('Error fetching overdue tasks:', err);
      setOverdueError('Failed to fetch overdue tasks.');
    } finally {
      setOverdueLoading(false);
    }
  }, [computeIsPastDue]);

  // Fetch overdue on Admin/Super Admin
  useEffect(() => {
    const role = String(user?.role || '').toLowerCase();
    if (user && (role === 'admin' || role === 'super_admin')) {
      fetchOverdueTasks();
      const intervalId = setInterval(fetchOverdueTasks, 300_000); // 5 minutes
      return () => clearInterval(intervalId);
    }
  }, [user, fetchOverdueTasks]);

  // Records list
  const fetchRecords = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, limit };
      if (filterCollector) params.collectorId = filterCollector;
      if (searchQuery) params.search = searchQuery;

      const response = await api.get('/records', { params });
      setRecords(response.data.data || []);
      setTotalRecords(response.data.total || 0);
      setSelectedRecords([]);
    } catch (err) {
      showMessage('Failed to fetch records.');
      console.error(err);
      setRecords([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [filterCollector, page, limit, searchQuery]);

  const fetchDuplicates = useCallback(async () => {
    try {
      setDuplicatesLoading(true);
      const response = await api.get('/records/duplicates');
      setDuplicateRecords(response.data || []);
      setSelectedRecords([]);
    } catch (err) {
      showMessage('Failed to fetch duplicate records.');
      console.error(err);
      setDuplicateRecords([]);
    } finally {
      setDuplicatesLoading(false);
    }
  }, []);

  useEffect(() => {
    setPage(1);
  }, [filterCollector, searchQuery]);

  useEffect(() => {
    const fetchCollectors = async () => {
      try {
        const response = await api.get('/users/collectors');
        setCollectors(response.data);
      } catch (err) {
        console.error('Failed to fetch collectors.', err);
        showMessage('Could not fetch the list of collectors.');
      }
    };
    fetchCollectors();
  }, []);

  useEffect(() => {
    if (!selectedRecord) {
      if (viewMode === 'records') {
        fetchRecords();
      } else if (viewMode === 'duplicates') {
        fetchDuplicates();
      }
    }
  }, [fetchRecords, fetchDuplicates, selectedRecord, viewMode]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
    setFailedRecords([]); // Clear errors when a new file is selected
  };

  const handleClearErrors = () => {
    setFailedRecords([]); // Manually close the popup
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) return showMessage('Please select a file to upload.');
    setIsUploading(true);
    setFailedRecords([]); // Reset previous errors
    const formData = new FormData();
    formData.append('file', selectedFile);
    if (uploadCollector) formData.append('collectorId', uploadCollector);

    try {
      const response = await api.post('/records/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Extract success count and failures from response
      // Supporting both 'failedRecords' and 'errors' naming conventions just in case
      const { count, failedRecords: failures, errors } = response.data;
      const actualFailures = failures || errors || [];

      if (actualFailures.length > 0) {
        // Set state to trigger the popup in UploadForm
        setFailedRecords(actualFailures);
        showMessage(`Uploaded ${count} records. ${actualFailures.length} records failed validation.`, 'warning');
      } else {
        showMessage(`Upload processed. ${count} records added successfully.`, 'success');
        setFailedRecords([]);
        setSelectedFile(null);
        setUploadCollector('');
        const form = document.getElementById('file-upload-form');
        if (form) form.reset();
        await fetchRecords();
      }

    } catch (err) {
      // If the entire request fails (e.g. 500 or 400)
      const errorMsg = err.response?.data?.message || 'File upload failed.';
      showMessage(errorMsg);
      console.error(err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleManualCreate = async (formData) => {
    try {
      const payload = {
        ...formData,
        bill: parseFloat(formData.bill) || null,
        crAmount: parseFloat(formData.crAmount) || null,
        claimNo: formData.claimNo.filter(item => item.value),
        adjNumber: formData.adjNumber.filter(item => item.value),
        doi: formData.doi.filter(item => item.value)
      };
      if (manualAssignCollector) payload.assignedCollector = manualAssignCollector;
      await api.post('/records', payload);
      showMessage('Record created successfully.', 'success');
      await fetchRecords();
      setCreationMode('upload');
      setManualAssignCollector('');
    } catch (err) {
      showMessage(`Failed to create record: ${err.response?.data?.message?.join(', ') || 'Unknown error'}.`);
      console.error(err.response || err);
    }
  };

  const handleAssignCollector = (recordId, collectorId) => {
    const executeAssign = async () => {
      try {
        const response = await api.put(`/records/${recordId}/assign`, { collectorId });
        const updatedRecord = response.data;
        showMessage('Collector assigned successfully.', 'success');

        setRecords(prev => prev.map(r => (r._id === recordId ? updatedRecord : r)));
        setDuplicateRecords(prev => prev.map(r => (r._id === recordId ? updatedRecord : r)));

        if (selectedRecord?._id === recordId) setSelectedRecord(updatedRecord);
      } catch (err) {
        showMessage('Failed to assign collector.');
        console.error(err);
      }
    };

    setModalConfig({
      isOpen: true,
      title: 'Assign Collector',
      message: 'Are you sure you want to assign this collector to the patient?',
      onConfirm: executeAssign,
      isDanger: false,
      confirmText: 'Assign'
    });
  };

  const handleSaveRecord = async (recordId, data) => {
    try {
      const { assignedCollector, comments, _id, __v, ...updatePayload } = data;
      const response = await api.put(`/records/${recordId}`, updatePayload);
      const updatedRecord = response.data;
      setRecords(prev => prev.map(r => (r._id === recordId ? updatedRecord : r)));
      setDuplicateRecords(prev => prev.map(r => (r._id === recordId ? updatedRecord : r)));
      setSelectedRecord(updatedRecord);
      return updatedRecord;
    } catch (error) {
      showMessage('Failed to update record.');
      console.error(error);
      throw error;
    }
  };

  const handleAlertSelect = async (recordId) => {
    try {
      const response = await api.get(`/records/${recordId}`);
      setSelectedRecord(response.data);
    } catch (err) {
      console.error('Failed to fetch record from alert:', err);
      showMessage('Could not load the selected record.');
    }
  };

  const handleSelectionChange = (recordId) => {
    setSelectedRecords(prev => (prev.includes(recordId) ? prev.filter(id => id !== recordId) : [...prev, recordId]));
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const source = viewMode === 'duplicates' ? duplicateRecords : records;
      setSelectedRecords(source.map(r => r._id));
    } else {
      setSelectedRecords([]);
    }
  };

  const handleDeleteSelectedRecords = () => {
    if (selectedRecords.length === 0) {
      return showMessage('Please select at least one record to delete.', 'error');
    }

    const executeDelete = async () => {
      try {
        await api.delete('/records/delete-many', { data: { ids: selectedRecords } });
        showMessage(`${selectedRecords.length} record(s) deleted successfully.`, 'success');
        if (viewMode === 'duplicates') {
          await fetchDuplicates();
        } else {
          await fetchRecords();
        }
      } catch (err) {
        showMessage(err.response?.data?.message || 'Failed to delete records.');
        console.error(err);
      }
    };

    setModalConfig({
      isOpen: true,
      title: 'Delete Records',
      message: `Are you sure you want to delete ${selectedRecords.length} record(s)? This action cannot be undone.`,
      onConfirm: executeDelete,
      isDanger: true,
      confirmText: 'Delete'
    });
  };

  const handleReassignRecords = (collectorId) => {
    if (selectedRecords.length === 0) {
      return showMessage('Please select at least one record to reassign.', 'error');
    }

    const executeReassign = async () => {
      try {
        await api.put('/records/reassign-many', {
          recordIds: selectedRecords,
          collectorId: collectorId
        });
        showMessage(`${selectedRecords.length} record(s) reassigned successfully.`, 'success');
        if (viewMode === 'duplicates') {
          await fetchDuplicates();
        } else {
          await fetchRecords();
        }
      } catch (err) {
        showMessage(err.response?.data?.message || 'Failed to reassign records.');
        console.error(err);
      }
    };

    setModalConfig({
      isOpen: true,
      title: 'Reassign Records',
      message: `Are you sure you want to reassign ${selectedRecords.length} record(s)?`,
      onConfirm: executeReassign,
      isDanger: false,
      confirmText: 'Reassign'
    });
  };
  // --- MERGE SELECTED DUPLICATES ---
  const openMergeSelectedModal = () => {
    if (viewMode !== 'duplicates') {
      return showMessage('Merge is only available in the Duplicate Records tab.', 'error');
    }
    if (!selectedRecords || selectedRecords.length < 2) {
      return showMessage('Please select at least 2 duplicate records to merge.', 'error');
    }
    // default primary = first selected
    setMergePrimaryId(selectedRecords[0]);
    setIsMergeModalOpen(true);
  };

  const confirmMergeSelected = async () => {
    if (!mergePrimaryId) {
      return showMessage('Please choose a primary record (the one to keep).', 'error');
    }

    const duplicateIds = selectedRecords.filter((id) => id !== mergePrimaryId);
    if (duplicateIds.length === 0) {
      return showMessage('Primary record cannot be the only selected record.', 'error');
    }

    try {
      setMergeSubmitting(true);
      const res = await api.post('/records/duplicates/merge-group', {
        primaryId: mergePrimaryId,
        duplicateIds,
      });

      showMessage(
        `Merge completed. Primary kept: ${mergePrimaryId}. Duplicates merged: ${res.data?.mergedRecords ?? duplicateIds.length}. Comments merged: ${res.data?.mergedComments ?? 0}.`,
        'success'
      );

      setIsMergeModalOpen(false);
      setSelectedRecords([]);
      await fetchDuplicates();
    } catch (err) {
      console.error('Merge failed:', err);
      showMessage(err.response?.data?.message || 'Merge failed. Check backend logs for details.');
    } finally {
      setMergeSubmitting(false);
    }
  };
  const navItems = [
    { label: 'Create Patient', icon: <PlusCircle className="w-4 h-4" />, mode: 'create' },
    { label: 'All Patients', icon: <FileText className="w-4 h-4" />, mode: 'records' },
    { label: 'Duplicate Records', icon: <AlertTriangle className="w-4 h-4" />, mode: 'duplicates' },
    { label: 'Schedule Calendar', icon: <Calendar className="w-4 h-4" />, mode: 'schedule' },
    { label: 'Register User', icon: <UserPlus className="w-4 h-4" />, mode: 'register' },
    { label: 'Manage Users', icon: <Users className="w-4 h-4" />, mode: 'users' },
    { label: 'Overdue Alerts', icon: <Bell className="w-4 h-4" />, mode: 'overdue' },
    { label: 'Summary', icon: <BarChart className="w-4 h-4" />, mode: 'summary' },
  ];

  const newOverdueCount = Math.max(0, overdueTasks.length - seenOverdueCount);

  const [lastNotifiedOverdue, setLastNotifiedOverdue] = useState(() => {
    return parseInt(localStorage.getItem('adminLastNotifiedOverdue') || '0', 10);
  });

  useEffect(() => {
    const currentTotal = overdueTasks.length;
    const unseen = Math.max(0, currentTotal - seenOverdueCount);

    if (unseen > 0 && currentTotal > lastNotifiedOverdue && viewMode !== 'overdue') {
      showMessage(
        `${unseen} overdue task${unseen > 1 ? 's' : ''} need your attention. Go to “Overdue Alerts” to review.`,
        'error',
        7000
      );
      setLastNotifiedOverdue(currentTotal);
      localStorage.setItem('adminLastNotifiedOverdue', String(currentTotal));
    }
  }, [overdueTasks.length, seenOverdueCount, viewMode, lastNotifiedOverdue]);

  const collectorsForDropdown = [{ _id: 'unassigned', username: 'Unassigned' }, ...collectors];

  if (selectedRecord) {
    return (
      <>
        <Header
          title="Admin Dashboard"
          user={user}
          onLogout={handleLogout}
          navItems={navItems}
          currentViewMode={'records'}
          onViewModeChange={(mode) => {
            setSelectedRecord(null);
            setViewMode(mode);
            if (mode === 'overdue') {
              setSeenOverdueCount(overdueTasks.length);
            }
          }}
          overdueTaskCount={newOverdueCount}
        />
        <RecordDetails
          record={selectedRecord}
          onBack={() => setSelectedRecord(null)}
          onSave={handleSaveRecord}
          onAssign={handleAssignCollector}
          collectors={collectorsForDropdown}
          user={user}
          api={api}
        />
        <ConfirmationModal 
          isOpen={modalConfig.isOpen}
          onClose={() => setModalConfig(prev => ({ ...prev, isOpen: false }))}
          onConfirm={modalConfig.onConfirm}
          title={modalConfig.title}
          message={modalConfig.message}
          confirmText={modalConfig.confirmText}
          isDanger={modalConfig.isDanger}
        />
      </>
    );
  }

  return (
    <>
      <Header
        title="Admin Dashboard"
        user={user}
        onLogout={handleLogout}
        navItems={navItems}
        currentViewMode={viewMode}
        onViewModeChange={(mode) => {
          setViewMode(mode);
          if (mode === 'overdue') {
            setSeenOverdueCount(overdueTasks.length);
          }
        }}
        overdueTaskCount={newOverdueCount}
      />

      <ConfirmationModal 
        isOpen={modalConfig.isOpen}
        onClose={() => setModalConfig(prev => ({ ...prev, isOpen: false }))}
        onConfirm={modalConfig.onConfirm}
        title={modalConfig.title}
        message={modalConfig.message}
        confirmText={modalConfig.confirmText}
        isDanger={modalConfig.isDanger}
      />
      {/* Merge Selected Duplicates Modal */}
      {isMergeModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-2xl rounded-xl bg-white shadow-xl">
            <div className="border-b px-6 py-4">
              <h3 className="text-lg font-bold text-gray-800">Merge Selected Duplicates</h3>
              <p className="text-sm text-gray-600 mt-1">
                Choose which record to keep as the <strong>Primary</strong>. All comments from the others will be copied into it, tagged with source info, then the duplicates will be deleted.
              </p>
            </div>

            <div className="px-6 py-4 max-h-[60vh] overflow-auto">
              <div className="space-y-3">
                {selectedRecords.map((id) => {
                  const r = (duplicateRecords || []).find((x) => String(x._id) === String(id));
                  const label = r
                    ? `${r.provider || 'N/A'} | ${r.ptName || 'N/A'} | ADJ: ${(r.adjNumber || []).map((a) => a.value).join(', ') || 'N/A'} | Collector: ${r.assignedCollector?.username || 'Unassigned'}`
                    : id;
                  return (
                    <label key={id} className="flex items-start gap-3 rounded-lg border p-3 hover:bg-gray-50 cursor-pointer">
                      <input
                        type="radio"
                        name="mergePrimary"
                        className="mt-1 h-4 w-4"
                        checked={mergePrimaryId === id}
                        onChange={() => setMergePrimaryId(id)}
                      />
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-gray-800 break-all">{id}</div>
                        <div className="text-sm text-gray-600 break-words">{label}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="border-t px-6 py-4 flex items-center justify-end gap-3">
              <button
                onClick={() => setIsMergeModalOpen(false)}
                disabled={mergeSubmitting}
                className="px-4 py-2 rounded-md border bg-white hover:bg-gray-50 disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                onClick={confirmMergeSelected}
                disabled={mergeSubmitting}
                className="px-4 py-2 rounded-md bg-green-600 text-white font-semibold hover:bg-green-700 disabled:bg-gray-400"
              >
                {mergeSubmitting ? 'Merging...' : 'Merge Now'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 font-sans">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
          {message.text && (
            <div
              className={`mb-6 p-4 rounded-xl border-l-4 ${
                message.type === 'success'
                  ? 'bg-green-50 border-green-500 text-green-800'
                  : 'bg-red-50 border-red-500 text-red-800'
              }`}
            >
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  {message.type === 'success' ? (
                    <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  ) : (
                    <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </div>
                  )}
                </div>
                <div className="ml-3">
                  <p className="font-medium">{message.text}</p>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-xl overflow-hidden">
            {viewMode === 'create' && (
              <>
                <div className="border-b border-gray-200 bg-gray-50/50">
                  <div className="flex">
                    <button
                      onClick={() => setCreationMode('upload')}
                      className={`flex items-center space-x-2 px-6 py-4 border-b-2 font-medium text-sm transition-all duration-200 ${
                        creationMode === 'upload'
                          ? 'border-blue-500 text-blue-600 bg-white '
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-white/50'
                      }`}
                    >
                      <Upload className="h-4 w-4" />
                      <span>Bulk Upload</span>
                    </button>
                    <button
                      onClick={() => setCreationMode('manual')}
                      className={`flex items-center space-x-2 px-6 py-4 border-b-2 font-medium text-sm transition-all duration-200 ${
                        creationMode === 'manual'
                          ? 'border-blue-500 text-blue-600 bg-white '
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-white/50'
                      }`}
                    >
                      <Edit3 className="h-4 w-4" />
                      <span>Manual Entry</span>
                    </button>
                  </div>
                </div>

                <div className="p-6">
                  {creationMode === 'upload' ? (
                    <UploadForm
                      onFileChange={handleFileChange}
                      onUpload={handleUpload}
                      collectors={collectors}
                      uploadCollector={uploadCollector}
                      setUploadCollector={setUploadCollector}
                      isUploading={isUploading}
                      failedRecords={failedRecords} // PASS FAILED RECORDS
                      onClearErrors={handleClearErrors} // PASS CLOSE HANDLER
                    />
                  ) : (
                    <ManualRecordForm
                      onManualCreate={handleManualCreate}
                      collectors={collectors}
                      assignedCollector={manualAssignCollector}
                      setAssignedCollector={setManualAssignCollector}
                    />
                  )}
                </div>
              </>
            )}

            {viewMode === 'records' && (
              <div className="p-6">
                <RecordsTable
                  records={records}
                  collectors={collectorsForDropdown}
                  filterCollector={filterCollector}
                  setFilterCollector={setFilterCollector}
                  searchQuery={searchQuery}
                  setSearchQuery={setSearchQuery}
                  onRecordSelect={setSelectedRecord}
                  loading={loading}
                  selectedRecords={selectedRecords}
                  onSelectionChange={handleSelectionChange}
                  onSelectAll={handleSelectAll}
                  onDeleteSelected={handleDeleteSelectedRecords}
                  onReassign={handleReassignRecords}
                  userRole={user?.role}
                  api={api}
                />

                <Pagination
                  page={page}
                  setPage={setPage}
                  limit={limit}
                  setLimit={setLimit}
                  total={totalRecords}
                />
              </div>
            )}

            {viewMode === 'duplicates' && (
              <div className="p-6">
                <DuplicateRecordsTable
                  records={duplicateRecords}
                  collectors={collectorsForDropdown}
                  filterCollector={filterCollector}
                  setFilterCollector={setFilterCollector}
                  searchQuery={searchQuery}
                  setSearchQuery={setSearchQuery}
                  onRecordSelect={setSelectedRecord}
                  loading={duplicatesLoading}
                  selectedRecords={selectedRecords}
                  onSelectionChange={handleSelectionChange}
                  onSelectAll={handleSelectAll}
                  onDeleteSelected={handleDeleteSelectedRecords}
                  onReassign={handleReassignRecords}
                  onMergeSelected={openMergeSelectedModal}                 
                  userRole={user?.role}
                />
              </div>
            )}

            {viewMode === 'schedule' && (
              <div className="p-6">
                <ScheduleCalendar user={user} onRecordSelect={setSelectedRecord} api={api} />
              </div>
            )}

            {viewMode === 'register' && (
              <div className="p-6">
                <RegisterUserForm />
              </div>
            )}

            {viewMode === 'users' && (
              <div className="p-6">
                <ManageUsers />
              </div>
            )}

            {viewMode === 'overdue' && (
              <div className="p-6">
                <OverdueTasksTab
                  tasks={overdueTasks}
                  onRecordSelect={handleAlertSelect}
                  loading={overdueLoading}
                  error={overdueError}
                />
              </div>
            )}

            {viewMode === 'summary' && (
              <div className="p-6">
                <SummaryTab api={api} />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminPage;